import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { AppDispatch, getSessionLogs } from "../store";
import { searchSessions } from "../store";
import { FaSignal } from "react-icons/fa";
import { getCredentialAnalysisStatus, getDevice, getQuizStatus, isUserOnline } from "../utils/sessionHelper";

const LogPage = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [searchFields, setSearchFields] = useState({
    ccEmail: "",
    payerEmail: "",
    officialEmail: "",
    sessionId: "",
  });

  const [results, setResults] = useState<any[]>([]);
  const [selectedLogs, setSelectedLogs] = useState<{ logs: any[]; audits: any[] } | null>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<"status" | "details" | "logs">("status");
  const [loading, setLoading] = useState(false);

  const isSearchEnabled = Object.values(searchFields).some((v) => v.trim() !== "");

  const downloadCSV = (data: any[]) => {
    const headers = [
      "Signer name",
      "Signer email",
      "CC recipient email",
      "Third party payer email",
      "FirmID",
      "UserID (official)",
      "Email (official)",
      "Number of session documents",
      "Date (scheduled)",
      "Date (completed)",
      "Session type (KBA or PK)",
      "Download page",
      "Acuity appointment ID",
      "Acuity appointment type"
    ];

    const rows = data.map((item) => {
      const scheduledAt = item.scheduledAt?._seconds
        ? new Date(item.scheduledAt._seconds * 1000).toLocaleString()
        : "";
      const completedAt = item.completedAt?._seconds
        ? new Date(item.completedAt._seconds * 1000).toLocaleString()
        : "";

      return [
        item.user?.fullName || "",
        item.user?.email || "",
        item.emailCopies?.join(", ") || "",
        item.payerEmail || "",
        item.firmId || "",
        item.official?.uid || "",
        item.official?.email || "",
        item.documentsCount || 0,
        scheduledAt,
        completedAt,
        item.typeOfNotarization === 1 ? "KBA" : "PK",
        `https://yourdomain.com/download/${item.id}`,
        item.appointmentId || "",
        item.appointmentType || "",
      ];
    });

    const csvContent =
      [headers, ...rows]
        .map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
        )
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `sessions-log-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSearchFields((prev) => ({ ...prev, [name]: value }));
  };

  const isOfficialOnline = useMemo(() => isUserOnline(selectedRow), [selectedRow]);
  const isSignerOnline = useMemo(
    () => isUserOnline(selectedRow, "signerLastOnlineAt"),
    [selectedRow]
  );


  const handleSearch = async () => {
    setSelectedRow(null);
    setLoading(true);
    try {
      const response = await dispatch(searchSessions(searchFields)).unwrap();
      setResults(response || []);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (row: any) => {
    setSelectedRow(row);
    setActiveTab("status");

    try {
      const response = await dispatch(getSessionLogs({
        sessionId: row.id
      })).unwrap();
      setSelectedLogs(response);
    } catch (error) {
      console.error("Failed to fetch logs", error);
      setSelectedLogs(null);
    }
  };

  return (
    <Wrapper>
      <Header>Sessions Search</Header>

      <SearchBar>
        <Input name="ccEmail" placeholder="CC recipient email" value={searchFields.ccEmail} onChange={handleInputChange} />
        <Input name="payerEmail" placeholder="Third party payer email" value={searchFields.payerEmail} onChange={handleInputChange} />
        <Input name="officialEmail" placeholder="Official email" value={searchFields.officialEmail} onChange={handleInputChange} />
        <Input name="sessionId" placeholder="Session ID" value={searchFields.sessionId} onChange={handleInputChange} />
        <SearchButton disabled={!isSearchEnabled || loading} onClick={handleSearch}>
          {loading ? "Searching..." : "Search"}
        </SearchButton>
      </SearchBar>

      <CSVButton onClick={() => downloadCSV(results)} disabled={results.length === 0}>
        Download CSV
      </CSVButton>

      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>Stage</th>
              <th>Session ID</th>
              <th>Created</th>
              <th>Scheduled</th>
              <th>Completed</th>
              <th>Signer name</th>
              <th>Official Email</th>
              <th>Download Link</th>
              <th>Acuity Appointment ID</th>
              <th>Acuity Appointment Type</th>
            </tr>
          </thead>
          <tbody>
            {results.map((row, index) => {
              const created = row.createdAt?._seconds ? new Date(row.createdAt._seconds * 1000).toLocaleString() : "--";
              const scheduled = row.scheduledAt?._seconds ? new Date(row.scheduledAt._seconds * 1000).toLocaleString() : "--";
              const completed = row.completedAt?._seconds ? new Date(row.completedAt._seconds * 1000).toLocaleString() : "--";
              return (
                <tr
                  key={index}
                  onClick={() => handleRowClick(row)}
                  style={{
                    backgroundColor: selectedRow?.id === row.id ? "#e6f2ff" : "transparent"
                  }}
                >            <td>{row.stage || "--"}</td>
                  <td>{row.id || "--"}</td>
                  <td>{created}</td>
                  <td>{scheduled}</td>
                  <td>{completed}</td>
                  <td>{row.user?.fullName || "--"}</td>
                  <td>{row.official?.email || "--"}</td>
                  <td>
                    <a
                      href={`https://demo.onlinenotary.us/?token=${row.security?.token}&sid=${row.security?.sid}#/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open
                    </a>

                  </td>
                  <td>{row.appointmentId || "--"}</td>
                  <td>{row.appointmentType || "--"}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrapper>


      {selectedRow && (
        <>
          <Tabs>
            <TabButton active={activeTab === "status"} onClick={() => setActiveTab("status")}>Status</TabButton>
            <TabButton active={activeTab === "details"} onClick={() => setActiveTab("details")}>Details</TabButton>
            <TabButton active={activeTab === "logs"} onClick={() => setActiveTab("logs")}>Logs</TabButton>
          </Tabs>

          <TabContent>
            {activeTab === "status" && (
              <>
                <SessionStatusBox>
                  <FirstColumnSessioStatus>
                    <Row>
                      <RowName>Principal:</RowName>
                      <RowText>{selectedRow?.user?.fullName || ""}</RowText>
                    </Row>

                    <Row>
                      <RowName>Credential analysis:</RowName>
                      <RowText>
                        {getCredentialAnalysisStatus(selectedRow).text}
                      </RowText>
                    </Row>

                    <Row>
                      <RowName>Identity proofing quiz:</RowName>
                      <RowText>{getQuizStatus(selectedRow).text}</RowText>
                    </Row>
                    {getDevice(selectedRow) && (
                      <Row>
                        <RowName>Principal device:</RowName>
                        <RowText>{getDevice(selectedRow)}</RowText>
                      </Row>
                    )}
                    <Gap />
                    <Row>
                      <RowName>Notary in-session?</RowName>
                      {isOfficialOnline ? <OnlineRowSign /> : <OfflineRowSign />}
                      {isOfficialOnline &&
                        selectedRow?.officialConnectivity?.level && (
                          <RowText>
                            {selectedRow?.officialConnectivity?.level} / 5{" "}
                            <FaSignal />
                          </RowText>
                        )}
                    </Row>

                    <Row>
                      <RowName>Signer in-session?</RowName>
                      {isSignerOnline ? <OnlineRowSign /> : <OfflineRowSign />}
                      {isSignerOnline && selectedRow?.signerConnectivity?.level && (
                        <RowText>
                          {selectedRow?.signerConnectivity?.level} / 5 <FaSignal />
                        </RowText>
                      )}
                    </Row>

                    <Row>
                      <RowName>Signer WebRTS status:</RowName>
                      {selectedRow?.userVideoJoined ? (
                        <OnlineRowSign />
                      ) : (
                        <OfflineRowSign />
                      )}
                      {selectedRow?.signerConnectivity &&
                        selectedRow?.signerConnectivity?.error && (
                          <RowText>
                            WebRTC Error: {selectedRow?.signerConnectivity?.error}
                          </RowText>
                        )}
                    </Row>

                    <Row>
                      <RowName>Session stage:</RowName>
                      <RowText>{selectedRow?.stage}</RowText>
                    </Row>
                    <Gap />
                    <Row>
                      <RowName>Signer email:</RowName>
                      <RowText>{selectedRow?.user?.email || ""}</RowText>
                    </Row>
                    {selectedRow?.userPhone && (
                      <Row>
                        <RowName>Signer phone:</RowName>
                        <RowText>{selectedRow?.userPhone || ""}</RowText>
                      </Row>
                    )}
                    <Gap />
                  </FirstColumnSessioStatus>

                </SessionStatusBox>

              </>
            )}

            {activeTab === "details" && (
              <pre>{JSON.stringify(selectedRow, null, 2)}</pre>
            )}

            {activeTab === "logs" && (
              selectedLogs ? (
                <pre>{JSON.stringify(selectedLogs, null, 2)}</pre>
              ) : (
                <p>Loading or no logs available...</p>
              )
            )}
          </TabContent>
        </>
      )}
    </Wrapper>
  );
};

export default LogPage;

const Wrapper = styled.div`
  padding: 2rem;
  max-width: 100vw;
  overflow-x: hidden;
`;


const Header = styled.h2`
  margin-bottom: 1rem;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const Input = styled.input`
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ccc;
  min-width: 200px;
`;

const SearchButton = styled.button<{ disabled?: boolean }>`
  background: ${({ disabled }) => (disabled ? "#ccc" : "#007bff")};
  color: white;
  padding: 10px 20px;
  border-radius: 6px;
  border: none;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
`;

const CSVButton = styled.button`
  background: #f1f1f1;
  padding: 10px 15px;
  border: 1px solid #ccc;
  margin-bottom: 1rem;
  cursor: pointer;
`;

const Table = styled.table`
  min-width: 1200px; /* Or however wide your table needs to be */
  width: 100%;
  border-collapse: collapse;
  cursor: pointer;

  th, td {
    padding: 10px;
    border: 1px solid #ddd;
    white-space: nowrap; /* Prevent wrapping */
  }

  tr:hover {
    background: #f1f1f1;
  }
`;

const Tabs = styled.div`
  display: flex;
  margin-top: 1.5rem;
  gap: 1rem;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 10px 20px;
  border: none;
  background-color: ${({ active }) => (active ? "#007bff" : "#e9ecef")};
  color: ${({ active }) => (active ? "#fff" : "#000")};
  border-radius: 5px;
  cursor: pointer;
`;

const TabContent = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid #ccc;
  background: #fafafa;
  border-radius: 6px;
`;

const TableWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  margin-bottom: 1rem;
`;


const SessionStatusBox = styled.div`
  display: flex;
  overflow: hidden; /* Hides scrollbars */
`;

const FirstColumnSessioStatus = styled.div`
  flex: 1;
`;
const Row = styled.div`
  display: flex;
  gap: 5.99px; /* Reduced by 5% */
  word-wrap: break-word;
  overflow-wrap: break-word;
  margin-bottom: 7.70px; /* Reduced by 5% */
  align-items: center;
`;

const RowText = styled.span`
  color: grey;
  font-size: 11.97px; /* Reduced by 5% */
`;

const RowName = styled.span`
  color: black;
  font-size: 12.31px; /* Reduced by 5% */
  font-weight: 342px; /* Reduced by 5% */
  word-wrap: break-word;
  overflow-wrap: break-word;
  cursor: not-allowed;
`;

const Gap = styled.div`
  height: 18.81px; /* Reduced by 5% */
`;

const OnlineRowSign = styled.span`
  border-radius: 4.28px; /* Reduced by 5% */
  display: inline-block;
  width: 8.55px; /* Reduced by 5% */
  height: 8.55px; /* Reduced by 5% */
  background-color: #00b050;
`;

const OfflineRowSign = styled.span`
  border-radius: 4.28px; /* Reduced by 5% */
  display: inline-block;
  width: 8.55px; /* Reduced by 5% */
  height: 8.55px; /* Reduced by 5% */
  background-color: #920606;
`;